// =========================================================================================@@
// Last Updated Date: Jan 31, 2023
// Last Updated By: Ajay
// Status Level: 2
// ===========================================================================================

import React, { useContext } from 'react'
import { Button, Grid, GridCell, ListMenu, ListMenuButton, Text, View } from 'oio-react'
import PropTypes from 'prop-types'
import { AddIcon } from 'assets/icons'
import { humanizeLocalPolicyPrincipalsName } from 'config/constants/initiativeApps'
import { LAYOUT_HEADER } from 'config/constants/ui'
import { initiativeUrl, initiativeSettingsPeopleUrl } from 'config/constants/urls'
import InitiativeAppMoreControl from 'src/sites/kits/Initiative/components/AppMoreControl'
import UserListItemDetailsCard from 'src/sites/kits/People/components/UserListItemDetailsCard'
import { BackButton, EmptyContentBlock, Title, Titlebar } from 'src/sites/kits/UI'
import Popover from 'src/sites/kits/Utils/Popover'
import { useUserList } from 'src/core/graphql/hooks'
import { InitiativeHierarchyContext } from 'src/sites/kits/Utils/InitiativeHierarchy'
import { useGlobalState } from 'src/sites/state'

const InitiativePeople = ({ paddingLeft, paddingRight, showMobileBackButton }) => {
   const { theme } = useGlobalState()
   const isHeaderGL = theme.tmpGlobalLayout === LAYOUT_HEADER

   const { initiative } = useContext(InitiativeHierarchyContext)
   const { appsPeopleCurrentUserCanView, currentUserCanEdit } = initiative
   const primaryRole = initiative.roles.find(r => r.type === 'primary')
   const policyPrincipals = initiative.appsPeoplePolicies.find(p => p.action === 'view')?.principals

   const { userList, fetchMore, loading } = useUserList({
      userRelationshipFilter: {
         entityType: 'initiative',
         entityId: initiative.id,
         hasRoleParticipantElement: {
            roleType: 'primary',
            status: 'active'
         }
      },
      withRelationship: true,
      withRelationshipEntityType: 'initiative',
      withRelationshipEntityId: initiative.id
   }, {
      skip: !appsPeopleCurrentUserCanView
   })

   const handleLoadMore = () => {
      fetchMore({
         variables: {
            start: userList.listInfo.nextCursor
         },
         updateQuery: (prevResult, { fetchMoreResult }) => {
            if (!fetchMoreResult) {
               return prevResult
            }

            return {
               ...prevResult,
               userList: {
                  ...prevResult.userList,
                  items: [
                     ...prevResult.userList.items,
                     ...fetchMoreResult.userList.items
                  ],
                  listInfo: fetchMoreResult.userList.listInfo
               }
            }
         }
      })
   }

   return (
      <>
         <Titlebar
            borderStyle="none"
            paddingLeft={paddingLeft}
            paddingRight={paddingRight}>
            <View display="flex">
               {showMobileBackButton && <BackButton linkTo={initiativeUrl(initiative)} />}
               <Title size="md">{primaryRole.name}</Title>
            </View>
            {/* TODO: Not technically correct, you need to be an admin to invite */}
            {/* See: #768, https://github.com/mother/api/issues/872 */}
            {currentUserCanEdit && (
               <View display="flex">
                  <Popover.Provider>
                     <Popover.Anchor>
                        <View
                           display="flex"
                           alignItems="center"
                           height="30px"
                           padding="0px 15px"
                           borderRadius="15px"
                           backgroundColor="#000">
                           <View marginRight="10px">
                              <AddIcon width="12px" height="12px" color="#fff" strokeWidth="2px" />
                           </View>
                           <Text size="1.5" lineHeight="80%" color="#fff" weight="medium">
                              Invite
                           </Text>
                        </View>
                     </Popover.Anchor>
                     <Popover.View
                        anchorOriginHorizontal="right"
                        anchorOriginVertical="top"
                        width="150px">
                        <View
                           float="left"
                           width="100%"
                           backgroundColor="#fff"
                           borderRadius="5px"
                           boxShadow="3px 3px 12px rgba(0,0,0,0.2)">
                           <ListMenu
                              activeBackgroundColor="#333"
                              activeTextColor="#fff"
                              buttonBorderRadius="4px"
                              buttonSize="md"
                              buttonPaddingHorizontal="12px"
                              showActiveArrow>
                              {initiative.roles.map(role => (
                                 <ListMenuButton
                                    key={role.id}
                                    linkTo={`${initiativeSettingsPeopleUrl(initiative, role.id)}/invited/new`}
                                    name={`New ${role.name}`}
                                 />
                              ))}
                           </ListMenu>
                        </View>
                     </Popover.View>
                  </Popover.Provider>
                  <InitiativeAppMoreControl
                     appName={primaryRole.name}
                     appEnabledFieldName="appsPeopleEnabled"
                  />
               </View>
            )}
         </Titlebar>
         {appsPeopleCurrentUserCanView && userList.items.length === 0 && (
            <EmptyContentBlock
               message={`No one in this ${initiative.type.nameSingular} to show yet`}
            />
         )}
         {appsPeopleCurrentUserCanView && userList.items.length > 0 && (
            <View
               paddingLeft={paddingLeft}
               margin="0px 0px 20px 0px">
               <Grid
                  columns={isHeaderGL ? '1[a-c] 2[d-f]' : '1[a-d] 2[e-f]'}
                  spacing="0px">
                  {userList.items.map(user => (
                     <GridCell key={user.id}>
                        <UserListItemDetailsCard
                           id={user.id}
                           avatarMedia={user.avatarMedia}
                           badges={user.badges}
                           fullName={user.fullName}
                           organization={user.organization}
                           position={user.position}
                        />
                     </GridCell>
                  ))}
               </Grid>
               {userList.listInfo.hasNext && (
                  <View
                     float="left"
                     width="100%"
                     display="flex"
                     justifyContent="center"
                     padding="12px 0px">
                     <Button
                        color="#000"
                        textColor="#fff"
                        size="xs"
                        onClick={handleLoadMore}
                        name="Load More"
                        mode={loading ? 'loading' : 'normal'}
                        rounded
                     />
                  </View>
               )}
            </View>
         )}
         <View
            borderTop={appsPeopleCurrentUserCanView && userList.items.length > 0 &&
               `1px solid ${theme.tmpContentLineColor}`}>
            <EmptyContentBlock
               message={`${humanizeLocalPolicyPrincipalsName(initiative, policyPrincipals)} can see the ${primaryRole.name} of this ${initiative.type.nameSingular}`}
            />
         </View>
      </>
   )
}

InitiativePeople.propTypes = {
   paddingLeft: PropTypes.string,
   paddingRight: PropTypes.string,
   showMobileBackButton: PropTypes.bool
}

InitiativePeople.defaultProps = {
   paddingLeft: undefined,
   paddingRight: undefined,
   showMobileBackButton: false
}

export default InitiativePeople
